import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Heart, CheckCircle2, ArrowRight, Shirt, Baby } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const ASSISTANCE_OPTIONS = [
  "Interview / professional clothing",
  "Children's clothing",
  "Work uniforms / shoes",
  "Winter / seasonal clothing",
];
const INCOME_OPTIONS = [
  "Under $15,000",
  "$15,000 - $25,000",
  "$25,001 - $40,000",
  "$40,001 - $60,000",
  "Over $60,000",
  "Prefer not to say",
];
const EMPLOYMENT_OPTIONS = [
  "Unemployed - seeking work",
  "Employed part-time",
  "Employed full-time",
  "Student",
  "Unable to work / disability",
  "Retired",
];
const ASSISTANCE_PROGRAMS = [
  "SNAP (Food Stamps)",
  "TANF",
  "Medicaid",
  "SSI / SSDI",
  "Housing Assistance (Section 8)",
  "Unemployment Benefits",
  "WIC",
  "None",
];
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const TIMES = ["Morning (8am-12pm)", "Afternoon (12pm-4pm)", "Evening (4pm-8pm)"];

const GetHelp = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city: "", state: "CO", zip_code: "",
    assistance_type: [], household_size: "", household_income: "",
    employment_status: "", employment_details: "",
    government_assistance: [], government_assistance_details: "",
    contact_days: [], contact_times: [], notes: "",
  });

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleArray = (key, value) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone ||
        !form.household_size || !form.household_income || !form.employment_status) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        household_size: parseInt(form.household_size, 10),
      });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="max-w-lg w-full" data-testid="gethelp-success">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank you for applying
            </h1>
            <p className="text-slate-600 mb-6">
              Your request has been received. A member of the Soar Your Visibility team will reach
              out to you during your preferred contact times to discuss next steps.
            </p>
            <Link to="/">
              <Button variant="outline" className="gap-2" data-testid="gethelp-back-home">
                Back to Home <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="gethelp-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <div className="w-14 h-14 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Heart className="w-7 h-7 text-amber-400" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            Get Help With Clothing
          </h1>
          <p className="text-slate-300 text-base max-w-xl mx-auto">
            Whether you need professional attire for a job interview, clothing for your children, or
            work essentials, we're here to help. Complete this short, confidential survey and our team
            will contact you.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Contact info */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Your Contact Information</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input className="mt-1" data-testid="input-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input className="mt-1" data-testid="input-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" className="mt-1" data-testid="input-email" value={form.email} onChange={(e) => set("email", e.target.value)} />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input className="mt-1" data-testid="input-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
              <div>
                <Label>City</Label>
                <Input className="mt-1" data-testid="input-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>State</Label>
                  <Input className="mt-1" data-testid="input-state" value={form.state} onChange={(e) => set("state", e.target.value)} />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input className="mt-1" data-testid="input-zip" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Type of help */}
          <Card>
            <CardHeader><CardTitle className="text-lg">What kind of help do you need?</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ASSISTANCE_OPTIONS.map((opt) => (
                <label key={opt} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50" data-testid={`assistance-${opt}`}>
                  <Checkbox checked={form.assistance_type.includes(opt)} onCheckedChange={() => toggleArray("assistance_type", opt)} />
                  <span className="text-sm text-slate-700 flex items-center gap-2">
                    {opt.includes("Children") ? <Baby className="w-4 h-4 text-amber-600" /> : <Shirt className="w-4 h-4 text-amber-600" />}
                    {opt}
                  </span>
                </label>
              ))}
            </CardContent>
          </Card>

          {/* Screening */}
          <Card>
            <CardHeader><CardTitle className="text-lg">Eligibility Screening</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Household Size *</Label>
                  <Input type="number" min="1" className="mt-1" data-testid="input-household-size" value={form.household_size} onChange={(e) => set("household_size", e.target.value)} placeholder="Number of people in home" />
                </div>
                <div>
                  <Label>Annual Household Income *</Label>
                  <Select value={form.household_income} onValueChange={(v) => set("household_income", v)}>
                    <SelectTrigger className="mt-1" data-testid="select-income"><SelectValue placeholder="Select range" /></SelectTrigger>
                    <SelectContent>
                      {INCOME_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Employment Status *</Label>
                <Select value={form.employment_status} onValueChange={(v) => set("employment_status", v)}>
                  <SelectTrigger className="mt-1" data-testid="select-employment"><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input className="mt-2" data-testid="input-employment-details" placeholder="Optional: employer, job title, or details" value={form.employment_details} onChange={(e) => set("employment_details", e.target.value)} />
              </div>

              <div>
                <Label>Government Assistance Received (select all that apply)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {ASSISTANCE_PROGRAMS.map((prog) => (
                    <label key={prog} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`govt-${prog}`}>
                      <Checkbox checked={form.government_assistance.includes(prog)} onCheckedChange={() => toggleArray("government_assistance", prog)} />
                      {prog}
                    </label>
                  ))}
                </div>
                <Input className="mt-2" data-testid="input-govt-details" placeholder="Optional: case number or additional details" value={form.government_assistance_details} onChange={(e) => set("government_assistance_details", e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Availability */}
          <Card>
            <CardHeader><CardTitle className="text-lg">When can we contact you?</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label>Available Days</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {DAYS.map((day) => (
                    <button type="button" key={day} onClick={() => toggleArray("contact_days", day)} data-testid={`day-${day}`}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.contact_days.includes(day) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                      {day}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Available Times</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TIMES.map((time) => (
                    <button type="button" key={time} onClick={() => toggleArray("contact_times", time)} data-testid={`time-${time}`}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.contact_times.includes(time) ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                      {time}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Anything else you'd like us to know?</Label>
                <Textarea className="mt-1" data-testid="input-notes" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500">Your information is kept confidential and used only to determine eligibility.</p>
            <Button type="submit" disabled={loading} className="bg-amber-600 hover:bg-amber-700 text-white gap-2 px-8" data-testid="submit-application-btn">
              {loading ? "Submitting..." : "Submit Application"}
              {!loading && <ArrowRight className="w-4 h-4" />}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GetHelp;
